import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CollapsibleCard } from '@/components/common';
import { EmptyState } from '@/components/ui';
import { customerActivityService, type CustomerActivityItem } from '@/api/services/customerActivity';
import { subscribeCustomerActivityRefresh } from '@/utils/customerActivityRefresh';
import { tActivity } from '@/i18n/activity';
import { resolveActivityActionCapabilities } from '@/utils/activityActions';
import { useMe } from '@/context/MeContext';
import ActivityCard from './ActivityCard';
import ActivityViewAllLink from './ActivityViewAllLink';

type Props = {
  limit?: number;
  searchId?: string;
  /** When this value changes, data reloads in the background without clearing the list. */
  refreshSignal?: unknown;
  /** Render without outer card/header when nested in CollapsibleCard. */
  embedded?: boolean;
};

export default function RecentActivityWidget({
  limit = 5,
  searchId,
  refreshSignal,
  embedded = false,
}: Props) {
  const { me } = useMe();
  const actionCapabilities = resolveActivityActionCapabilities(me);
  const [items, setItems] = useState<CustomerActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const load = useCallback(
    async (background = false) => {
      const keepVisible = background && loadedRef.current;
      if (!keepVisible) setLoading(true);
      try {
        const data = await customerActivityService.list({
          page_size: limit,
          search_id: searchId,
        });
        setItems(data.items);
        loadedRef.current = data.items.length > 0;
      } catch {
        if (!keepVisible) {
          setItems([]);
          loadedRef.current = false;
        }
      } finally {
        if (!keepVisible) setLoading(false);
      }
    },
    [limit, searchId],
  );

  useEffect(() => {
    loadedRef.current = false;
    void load(false);
  }, [load]);

  useEffect(() => {
    if (refreshSignal == null) return;
    void load(true);
  }, [load, refreshSignal]);

  useEffect(() => {
    return subscribeCustomerActivityRefresh(() => {
      void load(true);
    });
  }, [load]);

  const content = loading ? (
    <div className="loading-inline" role="status">
      <span className="spinner" aria-hidden />
      Loading…
    </div>
  ) : items.length === 0 ? (
    <EmptyState
      title={tActivity('activity.empty.title')}
      description={tActivity('activity.empty.description')}
    />
  ) : (
    <div className="recent-activity-widget__list">
      {items.map((item) => (
        <ActivityCard key={item.id} item={item} compact actions={actionCapabilities} />
      ))}
    </div>
  );

  if (embedded) {
    return <div className="recent-activity-widget recent-activity-widget--embedded">{content}</div>;
  }

  return (
    <section className="card section recent-activity-widget">
      <div className="recent-activity-widget__header">
        <h2>{tActivity('activity.recent.title')}</h2>
        <ActivityViewAllLink searchId={searchId} />
      </div>
      {content}
    </section>
  );
}

type CollapsibleSectionProps = Props & {
  storageKey: string;
  storageScope?: string;
  defaultExpanded?: boolean;
};

/** Collapsible dashboard section wrapping the recent-activity list. */
export function RecentActivityCollapsibleSection({
  storageKey,
  storageScope = 'dashboard',
  defaultExpanded = true,
  limit = 5,
  searchId,
  refreshSignal,
}: CollapsibleSectionProps) {
  return (
    <CollapsibleCard
      storageKey={storageKey}
      storageScope={storageScope}
      title={tActivity('activity.recent.title')}
      defaultExpanded={defaultExpanded}
      actions={<ActivityViewAllLink searchId={searchId} />}
    >
      <RecentActivityWidget
        embedded
        limit={limit}
        searchId={searchId}
        refreshSignal={refreshSignal}
      />
    </CollapsibleCard>
  );
}

/** @deprecated Use ActivityViewAllLink */
export { default as ActivityViewAllLink } from './ActivityViewAllLink';
export { default as AttentionViewAllLink } from './ActivityViewAllLink';
